import {
  required,
  badWords,
  disallowHtml,
  isEmail,
  maxLength,
  type Validator,
  minValue,
} from '@seek/validators-js';

import { useField } from '../../EnquiryForm';

export const useFormFields = () => {
  const getValidationValue = (value?: string[]) => value?.toString();

  const isTrueValidator: Validator<boolean> = {
    test: ({ value }) => value,
    getErrorMessage: () =>
      Promise.resolve('Please review the technical documentation'),
    getErrorMessageSync: () => 'Please review the technical documentation',
    name: 'isTrueValidator',
  };

  const firstName = useField({
    id: 'firstName',
    validators: [required, badWords, disallowHtml, maxLength] as any,
    validatorProps: { max: 100 },
  });
  const lastName = useField({
    id: 'lastName',
    validators: [required, badWords, disallowHtml, maxLength] as any,
    validatorProps: { max: 100 },
  });
  const jobTitle = useField({
    id: 'jobTitle',
    validators: [required, badWords, disallowHtml, maxLength] as any,
    validatorProps: { max: 100 },
  });
  const phone = useField({
    id: 'phone',
    validators: [required, disallowHtml, maxLength] as any,
    validatorProps: { max: 20 },
  });
  const email = useField({
    id: 'email',
    validators: [required, disallowHtml, isEmail, maxLength] as any,
    validatorProps: { max: 100 },
  });
  const company = useField({
    id: 'company',
    validators: [required, disallowHtml, maxLength] as any,
    validatorProps: { max: 100 },
  });
  const companyUrl = useField({
    id: 'companyUrl',
    validators: [required, disallowHtml, maxLength] as any,
    validatorProps: { max: 100 },
  });
  const partnerType = useField({
    id: 'partnerType',
    getValidationValue,
    validators: [required] as any,
  });
  const seekRsp = useField({
    id: 'seekRsp',
    validators: [required] as any,
  });

  const isSeekRsp = (): boolean => seekRsp.value === 'yes';

  const recruitmentSoftware = useField({
    id: 'recruitmentSoftware',
    validators: !isSeekRsp() ? ([required] as any) : [],
  });
  const solutions = useField({
    id: 'solutions',
    getValidationValue,
    validators: [required] as any,
  });
  const whySeek = useField({
    id: 'whySeek',
    validators: [required] as any,
  });
  const customerSegment = useField({
    id: 'customerSegment',
    validators: isSeekRsp() ? ([required, disallowHtml, maxLength] as any) : [],
    validatorProps: { max: 100 },
  });
  const numCustomers = useField({
    id: 'numCustomers',
    validators: [required, minValue] as any,
    validatorProps: { min: 0 },
  });

  const customersRequired: Validator<Array<{ seekId: string; name: string }>> =
    {
      test: ({ value }) => {
        const doesCustomersLengthMatch =
          Number(numCustomers.value) > 5
            ? value.length === 5
            : value.length === Number(numCustomers.value);

        return (
          doesCustomersLengthMatch &&
          !value.some(({ seekId, name }) => seekId === '' || name === '')
        );
      },
      getErrorMessage: () => Promise.resolve('Required field'),
      getErrorMessageSync: () => 'Required field',
      name: 'customersRequired',
    };

  const largestCustomers = useField({
    id: 'largestCustomers',
    validators: [customersRequired, disallowHtml] as any,
  });
  const integrateToSeek = useField({
    id: 'integrateToSeek',
    validators: [required],
  });

  const wantToIntegrate = (): boolean => integrateToSeek.value === 'yes';

  const reasonForIntegration = useField({
    id: 'reasonForIntegration',
    validators: !wantToIntegrate() ? ([required] as any) : [],
  });
  const currentlyIntegrated = useField({
    id: 'currentlyIntegrated',
    validators: [required],
  });
  const reviewDocs = useField({
    id: 'reviewDocs',
    validators: [isTrueValidator] as any,
  });

  return {
    company,
    companyUrl,
    currentlyIntegrated,
    customerSegment,
    email,
    firstName,
    integrateToSeek,
    jobTitle,
    largestCustomers,
    lastName,
    numCustomers,
    partnerType,
    phone,
    reasonForIntegration,
    recruitmentSoftware,
    reviewDocs,
    seekRsp,
    solutions,
    whySeek,
  };
};
